import {
  Undo2,
  Redo2,
  ZoomIn,
  ZoomOut,
  Maximize,
  RotateCcw,
  RotateCw,
  FlipHorizontal2,
  FlipVertical2,
  Save,
  FolderOpen,
  Grid3x3,
} from 'lucide-react';

interface EditorToolbarProps {
  fileName: string;
  isDirty: boolean;
  zoom: number;
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onFitToScreen: () => void;
  onRotateLeft: () => void;
  onRotateRight: () => void;
  onFlipH: () => void;
  onFlipV: () => void;
  onSave: () => void;
  onOpenFile: () => void;
  gridVisible: boolean;
  onToggleGrid: () => void;
}

export function EditorToolbar({
  fileName,
  isDirty,
  zoom,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  onZoomIn,
  onZoomOut,
  onFitToScreen,
  onRotateLeft,
  onRotateRight,
  onFlipH,
  onFlipV,
  onSave,
  onOpenFile,
  gridVisible,
  onToggleGrid,
}: EditorToolbarProps) {
  return (
    <div className="flex items-center h-12 bg-background border-b border-border px-2 gap-1">
      {/* File name */}
      <span className="text-sm font-medium truncate max-w-[200px] mx-2">
        {fileName}
        {isDirty && <span className="text-muted-foreground ml-1">*</span>}
      </span>

      <div className="w-px h-6 bg-border mx-1" />

      {/* Open file */}
      <ToolbarButton icon={FolderOpen} title="Open file" onClick={onOpenFile} />

      <div className="w-px h-6 bg-border mx-1" />

      {/* Undo/Redo */}
      <ToolbarButton icon={Undo2} title="Undo (Ctrl+Z)" onClick={onUndo} disabled={!canUndo} />
      <ToolbarButton
        icon={Redo2}
        title="Redo (Ctrl+Shift+Z)"
        onClick={onRedo}
        disabled={!canRedo}
      />

      <div className="w-px h-6 bg-border mx-1" />

      {/* Transform */}
      <ToolbarButton icon={RotateCcw} title="Rotate left 90deg" onClick={onRotateLeft} />
      <ToolbarButton icon={RotateCw} title="Rotate right 90deg" onClick={onRotateRight} />
      <ToolbarButton icon={FlipHorizontal2} title="Flip horizontal" onClick={onFlipH} />
      <ToolbarButton icon={FlipVertical2} title="Flip vertical" onClick={onFlipV} />

      <div className="w-px h-6 bg-border mx-1" />

      {/* Zoom */}
      <ToolbarButton icon={ZoomOut} title="Zoom out" onClick={onZoomOut} />
      <span className="text-xs text-muted-foreground w-12 text-center tabular-nums">
        {Math.round(zoom * 100)}%
      </span>
      <ToolbarButton icon={ZoomIn} title="Zoom in" onClick={onZoomIn} />
      <ToolbarButton icon={Maximize} title="Fit to screen" onClick={onFitToScreen} />

      <div className="w-px h-6 bg-border mx-1" />

      {/* Grid toggle */}
      <button
        type="button"
        className={`p-2 rounded-md transition-colors ${
          gridVisible ? 'bg-accent text-foreground' : 'text-muted-foreground hover:bg-accent'
        }`}
        title="Toggle transparency grid"
        onClick={onToggleGrid}
      >
        <Grid3x3 className="h-4 w-4" />
      </button>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Save */}
      <button
        type="button"
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90"
        onClick={onSave}
      >
        <Save className="h-4 w-4" />
        Save
      </button>
    </div>
  );
}

function ToolbarButton({
  icon: Icon,
  title,
  onClick,
  disabled,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      className="p-2 rounded-md hover:bg-accent text-muted-foreground disabled:opacity-40 disabled:pointer-events-none"
      title={title}
      onClick={onClick}
      disabled={disabled}
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}
